import { useMemo } from "react"
import { useSelector } from "react-redux"
import { usePortfolios } from "resources/portfolios"
import { useAccountAlias } from "resources/account"
import { getPortfolioMenuItems } from "modules/portfolio-root/selectors/menu"
import { getModels } from "resources/models"
import { getIsIntradayAccountEnabled } from "modules/account-root/selectors"
import { getIsOpPro } from "modules/account-root/selectors"
import { combineTanStackStatuses } from "helpers/data-statuses"
import { useResourcePermission } from "hooks/permissions"
import { useIsResourceFavorite } from "hooks/favorites"
import { matchATForecastByName } from "modules/portfolio-root/helpers"
import { useForecasts } from "resources/forecasts"
import { filterATForecasts } from "modules/resources/selectors"

/**
 * Hook to get portfolio menu items for dashboard when the instrument is a portfolio
 * @param {Object} instrument - The current dashboard instrument
 * @returns {Array|null} Array of menu items or null if not a portfolio
 */
export function useResourceMenuItems(instrument, callbacks) {
  const account = useAccountAlias()
  const models = useSelector(getModels)
  const intradayAccountEnabled = useSelector(getIsIntradayAccountEnabled)
  const isOpPro = useSelector(getIsOpPro)
  const [portfolios, portfoliosStatus] = usePortfolios()
  const [forecasts, forecastsStatus] = useForecasts()
  const [resourcePermission, resourcePermissionStatus] = useResourcePermission(instrument?.type, instrument?.id)
  const [isPortfolioFavorite, favoritesStatus] = useIsResourceFavorite(instrument?.id)

  const atForecasts = useMemo(() =>
    filterATForecasts(forecasts),
  [forecasts])

  const combinedStatus = useMemo(() =>
    combineTanStackStatuses([portfoliosStatus, resourcePermissionStatus, favoritesStatus, forecastsStatus]),
  [portfoliosStatus, resourcePermissionStatus, favoritesStatus, forecastsStatus],
  )

  return useMemo(() => {
    if (!instrument || instrument.type !== "PORTFOLIO") {
      return [null, combinedStatus]
    }

    if (!portfolios || !account) {
      return [null, combinedStatus]
    }

    const portfolioData = portfolios?.find(portfolio => portfolio.id === instrument.id)
    if (!portfolioData) {
      return [null, combinedStatus]
    }

    // Create portfolio object with metadata needed for menu items
    const portfolioWithMeta = {
      ...portfolioData,
      alias: portfolioData.alias || portfolioData.shortId,
      isFavorite: isPortfolioFavorite,
      permissionLevel: resourcePermission || "READ",
      isLinkedToAlphaTheory: matchATForecastByName(portfolioData.name, atForecasts),
    }

    return [getPortfolioMenuItems(account, portfolioWithMeta, models, intradayAccountEnabled, isOpPro, callbacks), combinedStatus]
  }, [instrument, portfolios, account, isPortfolioFavorite, resourcePermission, models, intradayAccountEnabled, isOpPro, atForecasts, combinedStatus, callbacks])
}
